"use client";

import { useUser } from "@clerk/nextjs";
import { useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { RequiredBuyPremiumButton } from "@/app/_components/required/buy-premium-button";

import { Slider } from "@/components/ui/slider";

import { formatBRL } from "@/helper/currency";
import { getUserPlanByMetadata } from "@/helper/user/client";
import { filterSearchParam } from "@/filters";

import { useFilter } from "../use-filter";

import type { PriceRange } from "@/types";

interface Props {
  priceRange: PriceRange;
}

export function Price({ priceRange }: Props) {
  const [
    { precoMin: priceMinSearchParam, precoMax: priceMaxSearchParam },
    setSearchParam,
  ] = useFilter();
  const { user } = useUser();
  const { hasPaidPlan } = getUserPlanByMetadata(user?.publicMetadata);

  // Ensure maxPrice is at least 20,000,000, but can be higher if priceRange.max is greater
  const maxPrice =
    priceRange.max === Infinity || priceRange.max < 15_000_000
      ? 15_000_000
      : priceRange.max;

  const priceMin = priceMinSearchParam || priceRange.min;
  const priceMax =
    priceMaxSearchParam === Infinity
      ? maxPrice
      : priceMaxSearchParam || maxPrice;

  const [prices, setPrices] = useState({
    min: Number(priceMin),
    max: priceMaxSearchParam === Infinity ? maxPrice : Number(priceMax),
  });

  const debounced = useDebouncedCallback((value) => {
    setSearchParam({
      [filterSearchParam.precoMin]: value.min,
      [filterSearchParam.precoMax]:
        value.max >= maxPrice ? Infinity : value.max,
    });
  }, 300);

  const formatPrice = (price: number) => {
    // Show "Sem limite" in these cases:
    // 1. When precoMax is explicitly set to "infinity"
    // 2. When there's no precoMax query parameter and price is at maxPrice
    // 3. When price is at maxPrice and no specific precoMax was provided
    if (
      (priceMaxSearchParam === Infinity || !priceMaxSearchParam) &&
      price >= maxPrice
    ) {
      return "Sem limite";
    }

    // For all other cases, show the formatted price
    return formatBRL(price);
  };

  return (
    <RequiredBuyPremiumButton>
      <div className="flex flex-col gap-4 p-4">
        <p className="text-sm">Preço mínimo e máximo</p>
        <div className="flex gap-2 items-center justify-between">
          <span className="text-sm text-muted-foreground min-w-[6rem]">
            {formatBRL(prices.min)}
          </span>
          <span className="text-sm text-muted-foreground min-w-[6rem]">
            {formatPrice(prices.max)}
          </span>
        </div>
        <Slider
          defaultValue={[prices.min, prices.max]}
          value={[prices.min, prices.max]}
          min={0}
          max={maxPrice}
          step={50_000}
          minStepsBetweenThumbs={1}
          onValueChange={(value) => {
            if (hasPaidPlan) {
              const [min, max] = value;
              const newValue = { min, max };

              setPrices(newValue);
              debounced(newValue);
            }
          }}
          className="min-w-[6rem]"
        />
      </div>
    </RequiredBuyPremiumButton>
  );
}
